/**
 * Cutsheet specification option values (SUP-311 / SUP-346).
 *
 * A specification's `options[]` is keyed by a freeform derived string, `value`.
 * That string is what a questionnaire response stores, and every reader —
 * the wizard chips, the review step, the printed PDF — resolves back to a
 * display label through it. Nothing ever guaranteed it was unique within a
 * spec, and two options collide easily: `value` is derived as
 * `printedText || label`, so an option whose "Printed Text" matches another
 * option's "Option Label" produces the same key.
 *
 * The consequences of a collision, all reported as separate bugs:
 *   - the builder form highlights BOTH colliding chips as selected, because
 *     selection is `value === optValue` (spec-field.tsx);
 *   - the printed PDF renders the WRONG option's label, because its lookup
 *     map is built with `optMap.set(opt.value, opt.label)` — last write wins —
 *     while the wizard resolves with `.find()` — first match wins. The two
 *     layers disagree about the same stored response, which is why "London
 *     Broil" printed on a Top Round the customer had set to "Cubed".
 *
 * Two helpers, used at the two different layers:
 *   - `deriveUniqueSpecOptionValues` on every WRITE, so a spec can never be
 *     saved with colliding values again;
 *   - `dedupeSpecOptionsByValue` on every READ, so specs already saved with
 *     collisions resolve deterministically (first wins, matching the wizard —
 *     the layer the customer actually saw) instead of differing per layer.
 */

/** Structural shape of a rich spec option. Options may also be plain strings. */
export interface SpecOptionLike {
  label?: string | null;
  /** Legacy alias for `label`. */
  title?: string | null;
  value?: string | null;
  printedText?: string | null;
  id?: string | null;
}

const text = (raw: unknown): string =>
  typeof raw === 'string' ? raw.trim() : '';

/**
 * Reads the stored key of an option, without deriving anything. Mirrors the
 * `value → label → title` fallback chain readers already use.
 * @param option A rich option object or a legacy plain-string option.
 */
export const specOptionValue = (option: string | SpecOptionLike): string =>
  typeof option === 'string'
    ? // Trimmed like every other branch. Both legacy migrations write `o.name`
      // untrimmed, and `resolveSpecDefaultValue` compares against a TRIMMED
      // default — so a padded option/default pair that was genuinely
      // selectable compared unequal and lost its pre-selection.
      text(option)
    : text(option.value) || text(option.label) || text(option.title);

/**
 * Recomputes `value` for a spec's whole `options[]` array, guaranteeing the
 * result is unique within that array.
 *
 * The base derivation is unchanged from before (`printedText || label || id`),
 * so the overwhelmingly common no-collision case produces exactly the values it
 * always did — existing responses keep resolving. Only an option that WOULD
 * have collided gets a different value, and it prefers its own label over a
 * synthetic suffix so the fallback text stays human-readable on a printed
 * sheet:
 *
 * 1. `printedText || label || id`  (today's derivation)
 * 2. `label`                       (an option printing under another's name
 * keeps its own name as the key)
 * 3. `printedText (label)`
 * 4. `<base> (2)`, `(3)`, …        (last resort — identical duplicates)
 * @param options The spec's options array, rich objects and/or legacy strings.
 * @returns A new array; rich options carry a unique `value`.
 */
export const deriveUniqueSpecOptionValues = <T extends SpecOptionLike>(
  options: ReadonlyArray<string | T>,
): Array<{ label: string; value: string } | T> => {
  const used = new Set<string>();

  /**
   * Claims the first unused candidate, else suffixes the base until free.
   * @param candidates Preferred keys in descending order of preference.
   */
  const claim = (candidates: string[]): string => {
    const wanted = candidates.filter(Boolean);
    const free = wanted.find((candidate) => !used.has(candidate));
    if (free) {
      used.add(free);
      return free;
    }
    // Every preferred key is taken — two options are genuinely identical.
    const base = wanted[0] || 'option';
    let suffix = 2;
    while (used.has(`${base} (${suffix})`)) suffix += 1;
    const unique = `${base} (${suffix})`;
    used.add(unique);
    return unique;
  };

  return options.map((option) => {
    if (typeof option === 'string') {
      const label = option.trim();
      return { label, value: claim([label]) };
    }
    const printed = text(option.printedText);
    const label = text(option.label) || text(option.title);
    const id = text(option.id);
    // `id` is a last-resort base so `value` is never empty for the rare option
    // carrying neither printedText nor label/title.
    const candidates = [printed || label || id];
    if (label) candidates.push(label);
    // Only meaningful when the option actually has BOTH — otherwise this
    // degrades to a value like " (Cubed)".
    if (printed && label) candidates.push(`${printed} (${label})`);
    return { ...option, value: claim(candidates) };
  });
};

/** Structural shape of the parts of a spec that constrain its default. */
export interface SpecDefaultLike {
  inputType?: string | null;
  options?: ReadonlyArray<string | SpecOptionLike> | null;
  defaultValue?: string | null;
}

/**
 * Returns a spec's `defaultValue` only when it is safe to seed as an answer,
 * and `undefined` otherwise (SUP-252).
 *
 * For `select` / `multiselect` the default has to resolve to one of the spec's
 * own options. The legacy → V2 capability migration carried defaults across
 * without re-mapping them to the new option labels, leaving specs whose default
 * is a bare `"1"` against options like `1lb`, or `"0.7"` against `3/4 in`.
 * Nothing rejected those on write, and every reader resolves a value through
 * the option list and falls through to printing the raw string when it finds no
 * match — which is the stray `1` customers saw on the Ground Beef pack size.
 *
 * Scope is deliberately narrow: this guards the DEFAULT, never an answer
 * already stored on a questionnaire response. Far more responses hold an
 * off-list answer because the processor edited the spec's options after the
 * cutsheet was filled out than because of a bad default, and those are real
 * customer selections — suppressing them would erase what someone actually
 * chose. Off-list stored answers keep rendering verbatim; only the unchosen
 * default is withheld.
 *
 * `text`/`number` specs have no option list to check against, so their default
 * passes through untouched.
 * @param spec The spec's `inputType`, `options`, and `defaultValue`.
 */
export const resolveSpecDefaultValue = (
  spec: SpecDefaultLike,
): string | undefined => {
  const defaultValue = text(spec.defaultValue);
  if (!defaultValue) return undefined;

  if (spec.inputType !== 'select' && spec.inputType !== 'multiselect') {
    return defaultValue;
  }

  // A choice spec with no options to choose from can only ever render its
  // default as raw text, which is the very thing being guarded against.
  const options = spec.options ?? [];
  if (options.length === 0) return undefined;

  return options.some((option) => specOptionValue(option) === defaultValue)
    ? defaultValue
    : undefined;
};

/**
 * Pairs an option in the PREVIOUSLY STORED list with its counterpart in the
 * incoming list: by `id` when both carry one, otherwise by position. Position
 * is the weaker rule but it is what the builder form preserves for an edit that
 * only changes an option's text.
 */
const findCounterpart = (
  previous: string | SpecOptionLike,
  previousIndex: number,
  incoming: ReadonlyArray<string | SpecOptionLike>,
): string | SpecOptionLike | undefined => {
  const id = typeof previous === 'string' ? '' : text(previous.id);
  // An identified option pairs by id ONLY. Falling back to position when the id
  // is absent from the incoming list would pair a DELETED option with whatever
  // now sits at its index — silently moving the default onto a different
  // option, which is worse than dropping it.
  if (id) {
    return incoming.find(
      (option) => typeof option !== 'string' && text(option.id) === id,
    );
  }
  return incoming[previousIndex];
};

/**
 * Resolves a spec's `defaultValue` when the option it points at may have had
 * its `value` rewritten, following the default to that option rather than
 * dropping it.
 *
 * A default is stored as an option's `value`, and `deriveUniqueSpecOptionValues`
 * recomputes that key from `printedText || label || id` — so it can legitimately
 * change for an option that was never broken. Adding Printed Text `"Mild
 * Flavor"` to the `Mild` option of a `Mild`/`Hot` spec rewrites that option's
 * value, and checking the old default `"Mild"` against the new list finds no
 * match. `SpecificationForm` has no defaultValue field, so a default dropped
 * that way cannot be put back from the UI.
 *
 * `previousOptions` — the options as CURRENTLY STORED on the spec — is what
 * makes that recoverable, and it is required for the rename case to work at
 * all. An earlier version matched against `spec.options` on the assumption
 * those were the pre-derivation options; they are not. `normalizeSpecOptions`
 * in the builder form runs `deriveUniqueSpecOptionValues` CLIENT-side before
 * the mutation is sent (see `use-capability-handlers` / `cutsheet-v2`), so the
 * API receives options whose `value` is already rewritten and the old value is
 * gone from the payload entirely. Matching a stale default against those found
 * nothing, and the guard then cleared a default that was never broken.
 *
 * Only a default that matches no option in either list — the SUP-252 case this
 * all exists for — is withheld.
 * @param spec The spec's `inputType`, `defaultValue`, and incoming options.
 * @param derivedOptions The incoming options after
 * {@link deriveUniqueSpecOptionValues}, in the same order.
 * @param previousOptions The options currently stored on the spec, when this is
 * an update. Omitted on create, where there is no prior option to follow.
 */
export const remapSpecDefaultValue = (
  spec: SpecDefaultLike,
  derivedOptions: ReadonlyArray<string | SpecOptionLike>,
  previousOptions?: ReadonlyArray<string | SpecOptionLike> | null,
): string | undefined => {
  // Already valid against the rewritten list (the common case: no option's
  // value changed), or a text/number default that needs no option at all.
  const resolved = resolveSpecDefaultValue({
    ...spec,
    options: derivedOptions,
  });
  if (resolved) return resolved;

  const defaultValue = text(spec.defaultValue);
  if (!defaultValue) return undefined;

  // Follow the default to the option it pointed at in the STORED list, then
  // read that option's new value from the incoming list.
  const previous = previousOptions ?? [];
  const previousIndex = previous.findIndex(
    (option) => specOptionValue(option) === defaultValue,
  );
  if (previousIndex >= 0) {
    const counterpart = findCounterpart(
      previous[previousIndex],
      previousIndex,
      derivedOptions,
    );
    if (counterpart) return specOptionValue(counterpart) || undefined;
  }

  // Fallback for a caller that hands over genuinely pre-derivation options
  // (no client-side normalization): match within the payload itself.
  const index = (spec.options ?? []).findIndex(
    (option) => specOptionValue(option) === defaultValue,
  );
  if (index < 0 || index >= derivedOptions.length) return undefined;

  return specOptionValue(derivedOptions[index]) || undefined;
};

/**
 * The DISPLAY text for a stored option value — what the wizard, the review step
 * and the printed PDF all show for a given answer.
 *
 * Readers resolve a stored value back through the option list rather than
 * printing it raw, because the value is a derived key (`printedText || label ||
 * id`) and is frequently not human-readable — an option keyed
 * `thickness-075` displays as `3/4 in`. A value matching no option falls
 * through to itself, matching the existing readers' behaviour for an answer
 * whose option was edited away.
 * @param options The spec's options array.
 * @param value The stored value to display.
 */
export const specOptionLabel = (
  options: ReadonlyArray<string | SpecOptionLike> | null | undefined,
  value: string | null | undefined,
): string => {
  const wanted = text(value);
  if (!wanted) return '';
  const match = (options ?? []).find(
    (option) => specOptionValue(option) === wanted,
  );
  if (!match) return wanted;
  if (typeof match === 'string') return text(match);
  return (
    text(match.printedText) || text(match.label) || text(match.title) || wanted
  );
};

/**
 * Drops options that repeat an earlier option's value, so a lookup built from
 * the result is unambiguous. First occurrence wins — the same option
 * `Array.prototype.find` already resolves to in the wizard, so a PDF built from
 * this agrees with what the customer saw on screen.
 *
 * Read-side only: this is how specs saved BEFORE the write-side guard existed
 * still render deterministically. Once such a spec is re-saved its options are
 * unique and nothing is dropped.
 * @param options The spec's options array.
 */
export const dedupeSpecOptionsByValue = <T extends SpecOptionLike>(
  options: ReadonlyArray<string | T>,
): Array<string | T> => {
  const seen = new Set<string>();
  return options.filter((option) => {
    const value = specOptionValue(option);
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};
